import React, { createContext, useContext, useState, useCallback } from 'react';
import { PodcastHost } from '@/hooks/usePodcastHosts';

interface PodcastHostDialogContextType {
  isOpen: boolean;
  mode: 'create' | 'edit';
  editingHost: PodcastHost | null;
  openCreateDialog: () => void;
  openEditDialog: (host: PodcastHost) => void;
  closeDialog: () => void;
}

const PodcastHostDialogContext = createContext<PodcastHostDialogContextType | undefined>(undefined);

export const PodcastHostDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [editingHost, setEditingHost] = useState<PodcastHost | null>(null);

  const openCreateDialog = useCallback(() => {
    setMode('create');
    setEditingHost(null);
    setIsOpen(true);
  }, []);

  const openEditDialog = useCallback((host: PodcastHost) => {
    setMode('edit');
    setEditingHost(host);
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setEditingHost(null);
  }, []);

  return (
    <PodcastHostDialogContext.Provider
      value={{
        isOpen,
        mode,
        editingHost,
        openCreateDialog,
        openEditDialog,
        closeDialog,
      }}
    >
      {children}
    </PodcastHostDialogContext.Provider>
  );
};

export const usePodcastHostDialog = () => {
  const context = useContext(PodcastHostDialogContext);
  if (!context) {
    throw new Error('usePodcastHostDialog must be used within PodcastHostDialogProvider');
  }
  return context;
};
